/* Shared */
import { Route } from 'application/Shared/Helpers';

/* Types */
import { User } from 'application/Shared/types/user';

/* React */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams, useSearchParams } from 'react-router';

import { ConnectionsAndRecipesChecker } from './Components/ConnectionsAndRecipesChecker';
import { ConnectionsAndRecipesContextProvider } from './Components/ConnectionsAndRecipesContext';

/* Components */
import { PageHeader } from './Components/PageHeader';
import { SaasOpsActionItemsStateBuilder } from './Components/SaasOpsActionStateBuilder';
import { SaasOpsUserAndAppSelection } from './Components/SaasOpsUserAndAppSelection';
import { AllowedActions } from './Components/types';
import { WorkatoActions } from './types';

const isAllowedAction = (action: AllowedActions) =>
    [WorkatoActions.offboard, WorkatoActions.onboard].includes(action);

const shouldRenderInitialStep = (
    action: AllowedActions,
    searchParams: URLSearchParams
) => {
    if (action === WorkatoActions.offboard) {
        const applicationId = searchParams.get('applicationId');
        const userId = searchParams.get('userId');
        const userEmail = searchParams.get('email');

        const hasUserInfo = !!userId || !!userEmail;

        return !applicationId || !hasUserInfo;
    }

    if (action === WorkatoActions.onboard) {
        const applicationId = searchParams.get('applicationId');
        const firstName = searchParams.get('firstName')?.trim();
        const lastName = searchParams.get('lastName')?.trim();
        const email = searchParams.get('email')?.trim();

        return !applicationId || !firstName || !lastName || !email;
    }

    return false;
};

/**
 * Main dashboard component for the SaasOps action workflow
 * @component
 * @returns {JSX.Element} SaasOps action dashboard with header and content
 */
export const SaasOpsActionDashboard = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();

    const { action, clientId, partnerId } = useParams<{
        action: AllowedActions;
        clientId: string;
        partnerId: string;
    }>();

    const baseAdminRoute = Route.buildBaseAdminRoute({
        clientId: clientId!,
        partnerId
    });

    // TODO: What should the result be here?
    // baseAdminRoute will return them to the client tenant's home page.
    // `PageNotFound` doesn't quite work b/c it still shows the sidebar.
    // We could move this check up into the Routes file which would let us properly show `PageNotFound`.
    if (!action || !isAllowedAction(action)) {
        return <Navigate to={baseAdminRoute} />;
    }

    const applicationId = searchParams.get('applicationId');

    const userId = searchParams.get('userId');
    const firstName = searchParams.get('firstName')?.trim();
    const lastName = searchParams.get('lastName')?.trim();
    const email = searchParams.get('email')?.trim();
    const name = searchParams.get('name')?.trim();

    // TODO: Should this just be built within SaasOpsActionStateBuilder?
    const userDetails = {
        email,
        firstName,
        id: userId,
        lastName,
        name
    } as Partial<User>;

    return (
        <React.Fragment key={action}>
            <PageHeader title={t(`SaasOps.${action}.title`)} />
            <ConnectionsAndRecipesContextProvider
                applicationId={applicationId}
                clientId={clientId!}
                recipeAction={action}
            >
                <ConnectionsAndRecipesChecker>
                    {shouldRenderInitialStep(action, searchParams) ? (
                        <SaasOpsUserAndAppSelection action={action} />
                    ) : (
                        <SaasOpsActionItemsStateBuilder
                            action={action}
                            userDetails={userDetails}
                        />
                    )}
                </ConnectionsAndRecipesChecker>
            </ConnectionsAndRecipesContextProvider>
        </React.Fragment>
    );
};
